import React, { FC, memo, useState, useEffect, useCallback } from 'react'

import { Element, scroller } from 'react-scroll'

import { getActiveRegionId } from '@sportworld/js-sdk/src/actions/region'
import { useAuth } from '@sportworld/js-sdk/src/context/authContext'

import { toggleAppSettings, getRegisteredUserId } from '@/actions/actions'
import { updateChannelEvents } from '@/actions/channelEvents'
import * as Button from '@/components/Button/Button'
import { Header } from '@/components/Header'
import { Toggle } from '@/components/Toggle/Toggle'
import { focusIds, orientation } from '@/constants/focus'
import { scrollOptions } from '@/constants/scrollers'
import { appSettingsIds } from '@/constants/settings'
import { EnvironmentSelection } from '@/containers/EnvironmentSelection'
import { clearUCStorage } from '@/containers/Usercentrics/usercentricsUtils'
import {
  getDuid,
  getUuid,
  getModelCode,
  getVersion,
  getModelName,
  getTotalMemory,
  getAvailableMemory,
} from '@/platform/platform'
import { platformName } from '@/platform/platformUtils'
import useStore from '@/store/useStore'
import { AppSettingsType } from '@/types/servicesTypes/configTypes'

import {
  SectionLayout,
  Section,
  SectionTitle,
  SectionContent,
  ToggleContainer,
  SettingsWrapper,
  SettingsRow,
  SettingsRowGroup,
  SettingsRowTitle,
} from './styles'
import { ScrollContainer, SettingsContainer } from '../styles'

const focusId = `${focusIds.SETTINGS_DEVELOPER}${focusIds.CONTAINER_MOD}`

const DeveloperRaw: FC = () => {
  const [sysParams, setSysParams] = useState<{
    memoryTotal: string | undefined
    memoryAvailable: string | undefined
  }>()
  const appSettings = useStore((state) => state.common.appSettings)
  const [defaultFocusRow, setDefaultFocusRow] = useState(0)
  const { isAuthenticatedUserType, isRegisteredUser } = useAuth()

  useEffect(() => {
    getSystemParams()
    const sysParamsInterval = setInterval(() => {
      getSystemParams()
    }, 10000)
    return () => {
      clearInterval(sysParamsInterval)
    }
  }, [])

  useEffect(() => {
    updateChannelEvents(true, [])
  }, [appSettings.isEnabledChannelEventMockData])

  const getSystemParams = () => {
    const memoryTotal = getTotalMemory()
    const memoryAvailable = getAvailableMemory()
    setSysParams({ memoryTotal, memoryAvailable })
  }

  const toggleHandler = useCallback(
    (settingsIds: string) => {
      toggleAppSettings({
        [settingsIds]: !appSettings[settingsIds as keyof AppSettingsType],
      })
    },
    [toggleAppSettings, appSettings],
  )

  const getMemoryDesc = () => {
    const totalMemoryDesc = sysParams?.memoryTotal
      ? `${sysParams?.memoryTotal} Mb`
      : ''
    const availableMemoryDesc = sysParams?.memoryAvailable
      ? `${sysParams?.memoryAvailable} Mb free`
      : ''
    return (
      totalMemoryDesc +
      `${totalMemoryDesc && availableMemoryDesc && ', '}` +
      availableMemoryDesc
    )
  }

  const regionId = getActiveRegionId()

  const onGridMove = (e: GridMoveEvent) => {
    const nextRowIndex = e.nextRowIndex
    const nextFocusId = e.gridNode.children[nextRowIndex]
    setDefaultFocusRow(nextRowIndex)
    scroller.scrollTo(nextFocusId, scrollOptions.DeveloperScroll)
  }

  return (
    <SettingsContainer
      focusId={focusId}
      elementType="div"
      orientation={orientation.VERTICAL}
      defaultFocusChild={0}
    >
      <Header />
      <SectionLayout>
        <SettingsWrapper
          defaultFocusRow={defaultFocusRow}
          onGridMove={onGridMove}
          isGrid
        >
          <ScrollContainer
            id={scrollOptions.DeveloperScroll.containerId}
            height={scrollOptions.DeveloperScroll.height}
          >
            <Element key={`${focusId}_0`} name={`${focusId}_0`}>
              <SettingsRow focusId={`${focusId}_0`} className="grid-row">
                <Section>
                  <SectionTitle>Platform</SectionTitle>
                  <SectionContent>
                    <p>
                      <span>Platform:</span> {platformName} {getVersion()}
                    </p>
                    <p>
                      <span>DUID:</span> {getDuid()}
                    </p>
                    <p>
                      <span>UUID:</span> {getUuid()}
                    </p>
                    <p>
                      <span>Model:</span> {getModelName()}
                    </p>
                    <p>
                      <span>Code:</span> {getModelCode()}
                    </p>
                    <p>
                      <span>Memory:</span> {getMemoryDesc() || ' not supported'}
                    </p>
                  </SectionContent>
                </Section>
                <Section>
                  <SectionTitle>Environment</SectionTitle>
                  <SectionContent>
                    <EnvironmentSelection />
                  </SectionContent>
                </Section>
              </SettingsRow>
            </Element>
            <Element key={`${focusId}_1`} name={`${focusId}_1`}>
              <SettingsRow focusId={`${focusId}_1`} className="grid-row">
                <Section>
                  <SectionTitle>User</SectionTitle>
                  <SectionContent>
                    <p>
                      <span>Type:</span> {isAuthenticatedUserType}
                    </p>
                    {isRegisteredUser && (
                      <p>
                        <span>UserId:</span> {getRegisteredUserId()}
                      </p>
                    )}
                    <p>
                      <span>Region:</span> {regionId}
                    </p>
                  </SectionContent>
                </Section>
              </SettingsRow>
            </Element>
            <Element key={`${focusId}_2`} name={`${focusId}_2`}>
              <SettingsRow focusId={`${focusId}_2`} className="grid-row">
                <Section>
                  <SectionTitle>Devtools</SectionTitle>
                  <SectionContent>
                    <ToggleContainer>
                      <p>Use Devtools</p>
                      <Toggle
                        checked={appSettings.isDevToolsUsed}
                        onChange={toggleHandler}
                        id={appSettingsIds.isDevToolsUsed}
                      />
                    </ToggleContainer>
                  </SectionContent>
                </Section>
                <Section>
                  <SectionTitle>Performance Monitor</SectionTitle>
                  <SectionContent>
                    <ToggleContainer>
                      <p>Use Performance Monitor</p>
                      <Toggle
                        checked={appSettings.isPerformanceMonitorUsed}
                        onChange={toggleHandler}
                        id={appSettingsIds.isPerformanceMonitorUsed}
                      />
                    </ToggleContainer>
                  </SectionContent>
                </Section>
              </SettingsRow>
            </Element>
            <Element key={`${focusId}_3`} name={`${focusId}_3`}>
              <SettingsRow focusId={`${focusId}_3`} className="grid-row">
                <Section>
                  <SectionTitle>Images</SectionTitle>
                  <SectionContent>
                    <ToggleContainer>
                      <p>Disable images</p>
                      <Toggle
                        checked={appSettings.isDisabledImages}
                        onChange={toggleHandler}
                        id={appSettingsIds.isDisabledImages}
                      />
                    </ToggleContainer>
                  </SectionContent>
                </Section>
                <Section>
                  <SectionTitle>Html widgets</SectionTitle>
                  <SectionContent>
                    <ToggleContainer>
                      <p>Disable html widgets</p>
                      <Toggle
                        checked={appSettings.isDisabledHtmlWidgets}
                        onChange={toggleHandler}
                        id={appSettingsIds.isDisabledHtmlWidgets}
                      />
                    </ToggleContainer>
                  </SectionContent>
                </Section>
              </SettingsRow>
            </Element>
            <Element key={`${focusId}_4`} name={`${focusId}_4`}>
              <SettingsRow focusId={`${focusId}_4`} className="grid-row">
                <Section>
                  <SectionTitle>Blur</SectionTitle>
                  <SectionContent>
                    <ToggleContainer>
                      <p>Disable Blur</p>
                      <Toggle
                        checked={appSettings.isDisabledBlur}
                        onChange={toggleHandler}
                        id={appSettingsIds.isDisabledBlur}
                      />
                    </ToggleContainer>
                  </SectionContent>
                </Section>
                <Section>
                  <SectionTitle>Themes</SectionTitle>
                  <SectionContent>
                    <ToggleContainer>
                      <p>Use Themes override</p>
                      <Toggle
                        checked={appSettings.isCustomPaletteUsed}
                        onChange={toggleHandler}
                        id={appSettingsIds.isCustomPaletteUsed}
                      />
                    </ToggleContainer>
                  </SectionContent>
                </Section>
              </SettingsRow>
            </Element>
            <Element key={`${focusId}_5`} name={`${focusId}_5`}>
              <SettingsRow focusId={`${focusId}_5`} className="grid-row">
                <Section>
                  <SectionTitle>Stage</SectionTitle>
                  <SectionContent>
                    <ToggleContainer>
                      <p>Disable autoslide and autofullscreen</p>
                      <Toggle
                        checked={appSettings.isDisabledStageAutoslide}
                        onChange={toggleHandler}
                        id={appSettingsIds.isDisabledStageAutoslide}
                      />
                    </ToggleContainer>
                  </SectionContent>
                </Section>
                <Section>
                  <SectionTitle>Lines</SectionTitle>
                  <SectionContent>
                    <ToggleContainer>
                      <p>Disable Lines</p>
                      <Toggle
                        checked={appSettings.isDisabledLines}
                        onChange={toggleHandler}
                        id={appSettingsIds.isDisabledLines}
                      />
                    </ToggleContainer>
                  </SectionContent>
                </Section>
              </SettingsRow>
            </Element>
            <SettingsRowGroup>
              <SettingsRowTitle>Video Playback</SettingsRowTitle>
              <Element key={`${focusId}_6`} name={`${focusId}_6`}>
                <SettingsRow focusId={`${focusId}_6`} className="grid-row">
                  <Section>
                    <SectionTitle>IMA SDK</SectionTitle>
                    <SectionContent>
                      <ToggleContainer>
                        <p>Use google Ads</p>
                        <Toggle
                          checked={appSettings.isEnabledImaSdk}
                          onChange={toggleHandler}
                          id={appSettingsIds.isEnabledImaSdk}
                          disabled
                        />
                      </ToggleContainer>
                    </SectionContent>
                  </Section>
                  <Section>
                    <SectionTitle>IMA SDK DEBUG</SectionTitle>
                    <SectionContent>
                      <ToggleContainer>
                        <p>Show google Ads debug overlays</p>
                        <Toggle
                          checked={appSettings.isEnabledImaSdkDebug}
                          onChange={toggleHandler}
                          id={appSettingsIds.isEnabledImaSdkDebug}
                        />
                      </ToggleContainer>
                    </SectionContent>
                  </Section>
                </SettingsRow>
              </Element>
              <Element key={`${focusId}_7`} name={`${focusId}_7`}>
                <SettingsRow focusId={`${focusId}_7`} className="grid-row">
                  <Section>
                    <SectionTitle>Override channel events</SectionTitle>
                    <SectionContent>
                      <ToggleContainer>
                        <p>Use 30-second events and 30-second breaks</p>
                        <Toggle
                          checked={appSettings.isEnabledChannelEventMockData}
                          onChange={toggleHandler}
                          id={appSettingsIds.isEnabledChannelEventMockData}
                        />
                      </ToggleContainer>
                    </SectionContent>
                  </Section>
                </SettingsRow>
              </Element>
            </SettingsRowGroup>
            <Element key={`${focusId}_8`} name={`${focusId}_8`}>
              <SettingsRow focusId={`${focusId}_8`} className="grid-row">
                <Section>
                  <SectionTitle>Consent</SectionTitle>
                  <SectionContent>
                    <ToggleContainer>
                      <p>
                        Clear Usercentrics Storage <br /> Restart the app
                      </p>
                      <Button.Button
                        text={'Reset'}
                        smallButton
                        onClick={clearUCStorage}
                        focusId={`${focusIds.BUTTON}ClearUCStorage`}
                      />
                    </ToggleContainer>
                  </SectionContent>
                </Section>
              </SettingsRow>
            </Element>
          </ScrollContainer>
        </SettingsWrapper>
      </SectionLayout>
    </SettingsContainer>
  )
}

export const Developer = memo(DeveloperRaw)
